import { memo, useState, useRef, useMemo, useCallback, ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  SelectsOptions,
  getSelectsOptions,
  notification,
  rejectionError,
  ValidationError,
  transformCrudFormErrors,
  RenderFormItem,
} from '@ohpen/enso-ui';
import {
  BaseRateRequest,
  UsageResponse,
  ChangeRequestOfBaseRateRequest,
  BaseRatesCreateChangeRequestResponse,
  VariabilityType,
  baseRatesCheckBaseRateUsage,
  baseRatesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

import { getBaseRatesFormSchema } from '../formConfig';
import { BaseRateCrudProps } from '../interfaces';

export const BaseRateCrudForm = memo(({ mode, record, draft, valueLists, refreshTable, onClose }: BaseRateCrudProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }
    const { code } = record || {};
    if (!code) {
      notification().error({ title: t('global.errors.unexpected') });
      onClose();
      return;
    }
    baseRatesCheckBaseRateUsage({ path: { baseRateCode: code } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
        onClose();
      });
  }, [mode, record, isUsed, t, onClose]);

  const onConfirm: (formData: BaseRateRequest) => Promise<ValidationError[] | void> = useCallback(
    data => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

      const body: ChangeRequestOfBaseRateRequest = {
        data,
        actionType: crudToActionMap[mode],
      };
      return baseRatesCreateChangeRequest({ body }).then(
        ({ data: { isValid, errors } }: { data: BaseRatesCreateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }
            const allErrors = transformCrudFormErrors({ ...errors });
            return allErrors;
          }
          if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
          notification().success({
            title: changeRequestNotificationMessage(t, enableFourEyes),
          });
        },
      );
    },
    [mode, refreshTable, t, enableFourEyes],
  );

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.baseRates.labels.baseRate') }),
      [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.baseRates.labels.baseRate') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.baseRates.labels.baseRateName') }),
    };
    return titles[mode];
  }, [mode, t]);

  const renderAlert: RenderFormItem<BaseRateRequest> = useCallback(
    (_schema, _config, _form) => {
      if (mode === CrudMode.EDIT && isUsed)
        return <Alert message={t('pmp.baseRates.labels.baseRateInUseMessage')} type="warning" showIcon />;
      return null;
    },
    [mode, isUsed, t],
  );

  const initialValues: BaseRateRequest | null = useMemo(() => {
    if (!record) return (draft as BaseRateRequest | undefined) ?? null;
    const { interestRates, variabilityType, ...props } = record;
    return {
      ...props,
      variabilityType: record?.variabilityType.key as VariabilityType,
      amount: record.rate,
    };
  }, [record, draft]);

  const crudProps: CrudProps<BaseRateRequest> = useMemo(() => {
    const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

    return {
      ref,
      open,
      schema: getBaseRatesFormSchema(selectsOptions, renderAlert, mode),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues,
      mode: mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [onConfirm, mode, open, t, title, valueLists, handleOnClose, initialValues, renderAlert, enableFourEyes]);

  return <Crud {...crudProps} />;
});
